// src/components/forms/OTAUploadForm.tsx

import { Form, Input, Upload, Button, message } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { useState, useMemo } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { useFileUpload } from '@/hooks/useFileUpload';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

/**
 * OTA 固件上传表单组件
 */
export const OTAUploadForm = ({
  onSubmit,
  currentVersion,
}: {
  onSubmit: (version: string, description: string, file: File) => Promise<void>;
  currentVersion?: string; // 当前版本号（升级时显示）
}) => {
  const [form] = Form.useForm();
  const { file, uploading, error, setUploadFile, uploadFile } = useFileUpload();
  const [version, setVersion] = useState('');
  const [description, setDescription] = useState('');

  // 使用文件固有属性生成稳定 uid
  const uploadFileList = useMemo<UploadFile[]>(() => {
    if (!file) return [];
    const stableId = `${file.name}-${file.size}-${file.lastModified}`;
    return [{
      uid: stableId,
      name: file.name,
      status: 'done',
      originFileObj: file as UploadFile['originFileObj'],
    }];
  }, [file]);

  /**
   * 处理文件选择
   */
  const handleFileChange: UploadProps['onChange'] = ({ fileList }) => {
    const selectedFile = fileList[fileList.length - 1]?.originFileObj || null;
    setUploadFile(selectedFile);
  };

  /**
   * 文件上传前验证
   */
  const beforeUpload = (file: File) => {
    // 验证文件大小 (5MB 限制)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      message.error('固件文件大小不能超过 5MB');
      return Upload.LIST_IGNORE;
    }

    // 验证文件类型
    const fileName = file.name.toLowerCase();
    const validExtensions = ['.bin', '.zip', '.ota', '.hex', '.fw'];
    const isValidType = validExtensions.some(ext => fileName.endsWith(ext));

    if (!isValidType) {
      message.error('仅支持 .bin/.zip/.ota/.hex/.fw 格式的固件文件');
      return Upload.LIST_IGNORE;
    }

    // 如果验证通过，继续处理文件
    setUploadFile(file);
    return false; // 阻止自动上传
  };

  /**
   * 处理表单提交
   */
  const onFinish = async () => {
    if (!version.trim()) {
      message.error('请输入固件版本号');
      return;
    }

    // 版本号格式验证 (x.x.x)
    if (!/^\d+\.\d+\.\d+$/.test(version)) {
      message.error('版本格式应为 x.x.x（例如：1.0.0）');
      return;
    }

    if (!file) {
      message.error('请选择固件文件');
      return;
    }

    await uploadFile(async () => {
      await onSubmit(version, description, file);
      setVersion('');
      setDescription('');
      form.resetFields(['version', 'description']);
    });
  };

  const acceptTypes = '.bin,.zip,.ota,.hex,.fw';

  // 文件类型图标
  const getFileTypeIcon = (fileName: string) => {
    const lowerName = fileName.toLowerCase();
    if (lowerName.endsWith('.bin')) return '📦';
    if (lowerName.endsWith('.zip')) return '🗜️';
    if (lowerName.endsWith('.ota')) return '🔄';
    if (lowerName.endsWith('.hex')) return '🔢';
    if (lowerName.endsWith('.fw')) return '⚙️';
    return '📦';
  };

  return (
    <div className="w-full">
      {error && (
        <ErrorAlert
          message="上传失败"
          description={error}
          className="mb-8"
          showIcon
        />
      )}

      <Form
        form={form}
        name="ota_upload"
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
        className="space-y-8"
      >
        <Form.Item
          name="version"
          label="固件版本"
          rules={[
            { required: true, message: '请输入固件版本' },
            { pattern: /^\d+\.\d+\.\d+$/, message: '版本格式应为 x.x.x（例如：1.0.0）' }
          ]}
          tooltip="请遵循语义化版本规范，主版本。次版本。补丁版本。"
          extra={currentVersion ? `当前版本：v${currentVersion}，新版本号必须大于当前版本` : '例如：1.0.0（首次上传推荐）'}
        >
          <Input
            placeholder="1.0.0"
            size="large"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            disabled={uploading}
            style={{ borderRadius: '8px' } as React.CSSProperties}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label="版本描述"
          tooltip="可选，描述本次固件更新的内容或修复的问题。"
        >
          <Input.TextArea
            placeholder="例如：修复蓝牙连接问题，优化功耗表现"
            rows={3}
            size="large"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={uploading}
            style={{ borderRadius: '8px' } as React.CSSProperties}
          />
        </Form.Item>

        {/* 固件文件上传组件 */}
        <Form.Item
          label="固件文件"
          required
          validateStatus={file ? 'success' : (uploading ? 'validating' : 'error')}
          help={!file && !uploading
            ? <span className="text-red-500">请选择固件文件</span>
            : file && (
                <span className="text-green-500 flex items-center">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  {file.name} · {Math.round(file.size / 1024)} KB
                </span>
              )
          }
        >
          <Upload.Dragger
            accept={acceptTypes}
            multiple={false}
            fileList={uploadFileList}
            onChange={handleFileChange}
            beforeUpload={beforeUpload}
            disabled={uploading}
            showUploadList={false}
            className="rounded-lg"
            style={{
              border: file ? '2px solid #52c41a' : '2px dashed #d9d9d9',
              background: file ? '#f0f9eb' : 'transparent',
              padding: file ? '24px' : '32px',
              transition: 'all 0.3s'
            } as React.CSSProperties}
          >
            {file ? (
              // 选中文件后展示预览
              <div className="flex flex-col items-center p-4 text-center">
                <div className="text-6xl mb-4">{getFileTypeIcon(file.name)}</div>
                <div className="font-medium text-lg mb-2 break-all max-w-full text-gray-800">
                  {file.name}
                </div>
                <div className="text-gray-600 text-sm mb-3">
                  {Math.round(file.size / 1024)} KB · {file.name.split('.').pop()?.toUpperCase()}
                </div>
                <div className="text-xs text-gray-500">
                  点击或拖拽其他文件可替换
                </div>
              </div>
            ) : (
              // 未选中文件时的拖拽样式
              <div className="text-center">
                <p className="ant-upload-drag-icon text-4xl text-gray-400 mb-3">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text text-lg font-medium text-gray-700 mb-2">
                  点击或拖拽文件到此区域上传
                </p>
                <p className="ant-upload-hint text-gray-500">
                  支持 .bin/.zip/.ota/.hex/.fw 文件 · 最大 5MB
                </p>
              </div>
            )}
          </Upload.Dragger>
        </Form.Item>

        <br />

        <Form.Item className="mb-0">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={uploading}
            disabled={uploading}
            className="w-full py-2 hover:shadow-md transition-shadow"
            style={{ borderRadius: '8px', fontSize: '16px' } as React.CSSProperties}
          >
            {uploading ? '上传中...' : (currentVersion ? '升级固件' : '上传固件')}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};